/**
 * Generic data grid for odl_app REST resources.
 *
 * Drives a MUI X DataGrid Pro grid entirely from a backend grid-spec
 * (`<basePath>/grid-spec/`) and its companion list endpoint (`<basePath>/`):
 * columns, default sort, page size, and column visibility all come from the
 * descriptor, so a caller only supplies render overrides and a cohesive
 * `options` bag for chrome and row interaction. This is the target shape for
 * odl_app grids — a list page with no hand-coded column structure.
 *
 * The `options` bag is forwarded to ServerDataGrid (which already supports
 * selection, toolbar/search slots, a stats-header child, pinned columns,
 * controlled column visibility, and mobile rendering), so a SiteGrid-class grid
 * can be expressed without dropping back to bespoke ServerDataGrid plumbing.
 *
 * Filter *vocabularies* come from the descriptor — which filters this requester
 * may use, and every value a closed one accepts — and reach the page as
 * filter-bar definitions when it takes `options.toolbarContent` as a function.
 * Where each control sits in the bar stays the page's call, as does any control
 * carrying an affordance the descriptor cannot state. A page still feeds its
 * own filter values back through `extraParams` (which the config param
 * serializer turns into `filter[...]`); a descriptor filter needs no such
 * wiring, since it writes the card-filter model the grid already merges.
 *
 * ODL-specific dependencies (API client, spec query, reference resolution,
 * param serialization, i18n, formatters) are read from DataGridConfigContext
 * via useDataGridConfig(). DataGridConfigProvider is mounted once at the app
 * root; grids outside the provider fall back to GENERIC_DEFAULTS automatically.
 *
 * Growing this component: prefer pushing new capability into the grid-spec
 * descriptor (so the backend drives it) or a cohesive `options` key over adding
 * another top-level prop.
 *
 * Contains:
 * - EntityGrid       — outer component: loads the spec, owns loading/error
 * - EntityGridContent — inner component: runs the grid once the spec is in hand
 */

import { Alert, Box, CircularProgress, Typography, useMediaQuery, useTheme } from '@mui/material';
import { GRID_CHECKBOX_SELECTION_FIELD } from '@mui/x-data-grid-pro';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import GridConfigureDialog from '@sophocles/data-grids/components/GridConfigureDialog';
import GridStatCards from '@sophocles/data-grids/components/GridStatCards';
import ServerDataGrid from '@sophocles/data-grids/components/ServerDataGrid';
import { DownloadButton } from '@sophocles/data-grids/components/toolbar';
import useDataGridConfig from '@sophocles/data-grids/config/useDataGridConfig';
import { buildGridListParams } from '@sophocles/data-grids/datasource/params';
import useGridCardFilters from '@sophocles/data-grids/datasource/useGridCardFilters';
import useGridPreferenceKey, { useGridSessionKey } from '@sophocles/data-grids/datasource/useGridPreferenceKey';
import useGridStats from '@sophocles/data-grids/datasource/useGridStats';
import useServerDataGrid from '@sophocles/data-grids/datasource/useServerDataGrid';
import useGridExport from '@sophocles/data-grids/export/useGridExport';
import { readStorage, writeStorage } from '@sophocles/hooks/usePersistedState';
import useUserPreference from '@sophocles/hooks/useUserPreference';

import { applyColumnOrder, columnSetStamp, readSavedColumnOrder, stampSavedColumnOrder } from './columnOrder';
import { buildColumnsFromSpec, buildColumnVisibilityModel } from './columnRenderers';
import { applyColumnWidths, readSavedColumnWidths, stampSavedColumnWidths } from './columnWidths';
import {
  buildDescriptorFilterControls,
  buildToolbarFilterDefs,
  restingFilterParams,
  splitQuickFilterDefs,
  unofferedFilterValues,
} from './descriptorFilters';
import { descriptorIcon } from './descriptorIcons';
import { buildPinnedColumns, normalizeDescriptorColumn } from './normalizeDescriptorColumn';
import { buildStatCardVisibilityModel, normalizeDescriptorStatCard } from './normalizeDescriptorStatCard';
import { resolveDescriptorLabel } from './resolveDescriptorLabel';
import { hasStatCardFilterBinding, statCardFilterParams, toFilterParams } from './statCardFilters';
import { applyStatCardOrder } from './statCardOrder';
import { odlResourcePath, resolveStatsFetcher } from './statsEndpoint';
import { useGridSpec } from './useGridSpec';

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/**
 * Converts the descriptor's default sort into a DataGrid sortModel. Prefers the
 * reshaped `default_order` array (`[{ field, dir }]`) and falls back to the
 * legacy `default_sort` ORM string (`"-requested_at"`) during the dual-emit
 * window. Returns `[]` when the descriptor declares no default.
 */
// Stable placeholders so the stats hook's effect deps never change for a grid
// that declares no stat cards.
const NOOP_FETCH_STATS = () => Promise.resolve(null);

/** The single column a mobile grid renders: one card per row, not a field. */
const MOBILE_SUMMARY_FIELD = 'summary';

function descriptorSortModel(descriptor) {
  const order = descriptor?.default_order;
  if (Array.isArray(order) && order.length > 0) {
    return order.map(({ field, dir }) => ({ field, sort: dir === 'desc' ? 'desc' : 'asc' }));
  }
  const ds = descriptor?.default_sort;
  if (!ds || typeof ds !== 'string') return [];
  return ds.startsWith('-') ? [{ field: ds.slice(1), sort: 'desc' }] : [{ field: ds, sort: 'asc' }];
}

/** Reads a sessionStorage-persisted row-id array; `[]` on any miss or error. */
function readPersistedSelectionIds(key) {
  const parsed = readStorage(key, []);
  return Array.isArray(parsed) ? parsed : [];
}

function writePersistedSelectionIds(key, ids) {
  writeStorage(key, ids);
}

// ---------------------------------------------------------------------------
// Inner: the grid, with the descriptor already resolved
// ---------------------------------------------------------------------------

/**
 * Renders the grid once the spec is loaded. Split from EntityGrid so that
 * useServerDataGrid mounts with the descriptor-derived sort/page size already
 * in hand (those initialize once, so they must be correct on first mount).
 */
function EntityGridContent({
  basePath,
  descriptor,
  specColumns,
  extraParams,
  fieldOverrides,
  actionHandlers,
  fetchFn,
  rows,
  options,
}) {
  const config = useDataGridConfig();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const {
    showSearch = true,
    searchPlaceholder,
    searchSlot,
    countNoun,
    createAction,
    toolbarContent,
    toolbarActions,
    toolbarVariant,
    persistKey,
    // false renders descriptor reference columns as plain chips instead of
    // entity links — for grids whose row click is the primary target.
    referenceLinks = true,
    infiniteScroll = false,
    defaultSort,
    gridApiRef,
    onGridReady,
    onTotalRowCountChange,
    onRowClick,
    selection,
    statsHeader,
    statCards,
    terms,
    mobile,
    extraColumns,
    columnVisibility,
    showGridSettings,
    gridProps,
    export: exportOptions,
  } = options;

  // Build the odl-app-prefixed path used by the default list fetch.
  const odlPath = odlResourcePath(basePath);

  // --- Card filters ------------------------------------------------------
  // The filter a stat-card click applies. The grid owns the model unless the
  // page renders its own chips over the same params, in which case the page
  // owns it and passes it in — the same controlled/uncontrolled split as
  // columnVisibility, and what keeps a card and a chip over one param from
  // holding different values.
  const ownedCardFilters = useGridCardFilters(persistKey);
  const cardFilters = statCards?.filters ?? ownedCardFilters;

  // A page-owned model is already folded into the caller's extraParams (it has
  // to be — the same values drive the page's bulk-action filters), so only the
  // grid's own model is merged here. An unapplied model passes extraParams
  // through by reference: useServerDataGrid recreates its dataSource on a new
  // params object, and a fresh one every render refetch-loops.
  //
  // A filter the grid rests on rides underneath all of it: the grid opens where
  // it says it does without every page sending that value itself. Anything the
  // caller or the reader has answered wins over it — a resting value is where
  // nobody has spoken yet.
  const effectiveExtraParams = useMemo(() => {
    const resting = restingFilterParams(descriptor?.filters, cardFilters.model);
    const base = Object.keys(resting).length === 0 ? extraParams : { ...resting, ...extraParams };
    if (statCards?.filters) return base;
    const cardParams = toFilterParams(ownedCardFilters.model);
    if (Object.keys(cardParams).length === 0) return base;
    return { ...base, ...cardParams };
  }, [extraParams, statCards?.filters, ownedCardFilters.model, descriptor?.filters, cardFilters.model]);

  // Default list fetch: GET odl-app/<basePath>/. The fetch function is injected
  // via config.fetchFn (ODL supplies apiClient; generic contexts return empty
  // rows). The caller's fetchFn prop wins over this default for union endpoints.
  const defaultListFetch = useCallback(
    async params => {
      if (!config.fetchFn) return { data: [], meta: {} };
      return config.fetchFn(odlPath, params);
    },
    [config.fetchFn, odlPath],
  );

  // Sort: caller override wins; otherwise the descriptor's default drives it.
  const sortModel = defaultSort ?? descriptorSortModel(descriptor);

  const gridState = useServerDataGrid({
    fetchFn: fetchFn ?? defaultListFetch,
    defaultSort: sortModel,
    // Pass the params through untouched: useServerDataGrid defaults an absent
    // value to a stable module-level EMPTY_OBJECT. A `?? {}` here would mint a
    // fresh object every render, recreating the dataSource and refetch-looping
    // (worse under infiniteScroll — visible rows clear on each dataSource swap).
    extraParams: effectiveExtraParams,
    infiniteScroll,
    persistKey,
    defaultPageSize: descriptor?.page_size,
    apiRef: gridApiRef,
    // Param serialization is injected via config rather than hardcoded to
    // 'odl-app'. config.paramSerializer = toJsonApiParams in the ODL context;
    // identity pass-through in generic contexts. paramStyle is intentionally
    // omitted here (config.paramSerializer wins over the paramStyle branch).
    paramSerializer: config.paramSerializer,
  });

  // MUI X's dataSource error path never reaches the line that clears the grid's
  // loading flag (the lazy-loading strategy's handleDataUpdate short-circuits on
  // `'error' in params` before it gets there), so a failed request spins the
  // overlay forever unless something clears it and surfaces the failure instead.
  const [dataSourceError, setDataSourceError] = useState(null);
  const handleDataSourceError = useCallback(
    error => {
      gridState.apiRef?.current?.setLoading?.(false);
      console.error('EntityGrid data source error:', error);
      setDataSourceError(error);
    },
    [gridState.apiRef],
  );
  // A new dataSource means a new request cycle (filter/sort/refresh) — drop a
  // stale error banner rather than let it outlive the request that caused it.
  useEffect(() => {
    setDataSourceError(null);
  }, [gridState.dataSource]);

  // CSV export (opt-in via options.export). The export walks the same endpoint
  // the grid reads, under the same filter/sort/search params — only the paging
  // window differs — so the file is the list on screen rather than a second,
  // separately-derived query.
  const [exportError, setExportError] = useState(null);
  const exportFetchPage = useCallback(
    ({ limit, offset }) => {
      const listFetch = fetchFn ?? defaultListFetch;
      const params = buildGridListParams({
        extraParams: effectiveExtraParams,
        sortModel: gridState.sortModel,
        quickFilterValues: gridState.filterModel?.quickFilterValues,
      });
      return listFetch(config.paramSerializer({ ...params, limit, offset }));
    },
    [
      fetchFn,
      defaultListFetch,
      effectiveExtraParams,
      gridState.sortModel,
      gridState.filterModel,
      config.paramSerializer,
    ],
  );
  const { runExport, isExporting } = useGridExport({
    specColumns,
    apiRef: gridState.apiRef,
    fetchPage: exportFetchPage,
    // Resource name over the odl_app path: 'sites/1/cameras' exports cameras.csv.
    fileName: exportOptions?.fileName ?? `${basePath.split('/').pop()}.csv`,
    overrides: exportOptions?.columns,
    extraColumns: exportOptions?.extraColumns,
    terms,
    // Page at the endpoint's declared ceiling: it clamps anything larger, and
    // a clamped page is indistinguishable from a last page to a caller that
    // never looks at the reported total.
    pageSize: descriptor?.max_page_size,
    onError: setExportError,
  });

  const desktopColumns = useMemo(
    () =>
      buildColumnsFromSpec(specColumns, fieldOverrides, actionHandlers, {
        t: config.translate,
        formatters: config.formatters,
        resolveReference: config.resolveReference,
        resolveActionIcon: config.resolveActionIcon,
        terms,
        referenceLinks,
      }),
    [
      specColumns,
      fieldOverrides,
      actionHandlers,
      config.translate,
      config.formatters,
      config.resolveReference,
      config.resolveActionIcon,
      terms,
      referenceLinks,
    ],
  );

  // Column order: user drag-reorders persist per grid and customer type
  // and re-apply through the columns prop — MUI derives column order from
  // prop order, so the saved preference stays the single source of truth for
  // the columns it names.
  //
  // Only while it still describes the grid it was saved for: the saved list is
  // stamped with the descriptor's column set, and a stamp that no longer
  // matches is dropped in favour of the descriptor's own order. Otherwise a
  // backend change to the columns or their default order never reaches anyone
  // who has ever dragged one.
  const columnOrderPrefKey = useGridPreferenceKey(persistKey, 'columnOrder');
  const [savedColumnOrder, setSavedColumnOrder] = useUserPreference(columnOrderPrefKey, null);
  const columnStamp = useMemo(() => columnSetStamp(specColumns), [specColumns]);
  const appliedColumnOrder = useMemo(
    () => readSavedColumnOrder(savedColumnOrder, columnStamp),
    [savedColumnOrder, columnStamp],
  );

  // Column widths: a drag-resize persists per grid and re-applies through the
  // columns prop, because MUI recomputes a flex column's width from its share
  // on every layout pass and would otherwise discard the one the user chose.
  const columnWidthPrefKey = useGridPreferenceKey(persistKey, 'columnWidths');
  const [savedColumnWidths, setSavedColumnWidths] = useUserPreference(columnWidthPrefKey, null);
  const appliedColumnWidths = useMemo(
    () => readSavedColumnWidths(savedColumnWidths, columnStamp),
    [savedColumnWidths, columnStamp],
  );

  // Mobile single-column: when a renderCard is supplied, collapse to one
  // full-width "summary" column that renders each row as a card. ServerDataGrid
  // strips the header chrome on mobile; only the columns array changes here.
  // On desktop, any caller `extraColumns` (bespoke columns the descriptor does
  // not own, e.g. an action button) are appended after the descriptor columns,
  // and the user's saved column order is applied over the merged set.
  const columns = useMemo(() => {
    if (isMobile && mobile?.renderCard) {
      return [
        {
          field: MOBILE_SUMMARY_FIELD,
          headerName: '',
          flex: 1,
          sortable: false,
          filterable: false,
          disableColumnMenu: true,
          renderCell: ({ row }) => mobile.renderCard(row),
        },
      ];
    }
    let merged = desktopColumns;
    if (extraColumns?.length) {
      // A caller extraColumn supersedes any descriptor column sharing its
      // field. Two defs with one field corrupt MUI's column state — the
      // duplicate is re-pushed into orderedFields on every columns-prop
      // update, so the grid sprouts repeated, misrendered columns.
      const extraFields = new Set(extraColumns.map(col => col.field));
      merged = [...desktopColumns.filter(col => !extraFields.has(col.field)), ...extraColumns];
    }
    return applyColumnWidths(applyColumnOrder(merged, appliedColumnOrder), appliedColumnWidths);
    // Depend on mobile.renderCard, not the whole `mobile` object — callers pass
    // an inline options literal, so a new `mobile` ref every render would churn
    // the columns array and re-render the grid needlessly.
  }, [isMobile, mobile?.renderCard, desktopColumns, extraColumns, appliedColumnOrder, appliedColumnWidths]);

  // Persist the order the drag left behind. Read back from the grid api (the
  // event only carries the moved column), excluding the generated selection
  // checkbox — it is position-locked, never part of the saved order.
  const handleColumnOrderChange = useCallback(() => {
    const api = gridState.apiRef?.current;
    if (!api?.getAllColumns) return;
    const fields = api
      .getAllColumns()
      .map(col => col.field)
      .filter(field => field !== GRID_CHECKBOX_SELECTION_FIELD);
    setSavedColumnOrder(stampSavedColumnOrder(fields, columnStamp));
  }, [gridState.apiRef, setSavedColumnOrder, columnStamp]);

  // Persist the width the drag left behind. Recorded against the current stamp,
  // which also discards widths saved for a column set the backend has changed.
  const handleColumnWidthChange = useCallback(
    ({ colDef, width }) => {
      if (!colDef?.field || !width) return;
      setSavedColumnWidths(previous => {
        const kept = readSavedColumnWidths(previous, columnStamp) ?? {};
        return stampSavedColumnWidths({ ...kept, [colDef.field]: width }, columnStamp);
      });
    },
    [setSavedColumnWidths, columnStamp],
  );

  const checkboxSelection = Boolean(selection?.enabled) && !isMobile;

  // initialState carries the spec-derived default visibility (unless the caller
  // drives visibility controlled, which would collide with initialState) plus
  // any pinned columns (desktop only). Built as one object so DataGridPro reads
  // it once on mount.
  const initialState = useMemo(() => {
    const state = {};
    if (!columnVisibility) {
      state.columns = { columnVisibilityModel: buildColumnVisibilityModel(specColumns) };
    }
    // Pinned columns (desktop only): the descriptor owns which columns pin, and
    // the generated selection checkbox anchors with them whenever it is on.
    if (!isMobile) {
      const effectivePinned = buildPinnedColumns(specColumns, checkboxSelection ? GRID_CHECKBOX_SELECTION_FIELD : null);
      if (effectivePinned) {
        state.pinnedColumns = effectivePinned;
      }
    }
    return state;
  }, [specColumns, columnVisibility, checkboxSelection, isMobile]);

  // Controlled visibility: layer the caller's persisted model OVER the
  // descriptor's default_hidden set, so the backend descriptor stays the single
  // source of truth for which columns start hidden (e.g. payload columns folded
  // into a rich name cell, or the raw id column) while the user's explicit
  // show/hide toggles still win. Without this merge a controlled model fully
  // replaces the descriptor default, so a grid whose persisted model omits a
  // folded column re-shows it next to the rich cell (the duplicate-column bug).
  const effectiveColumnVisibilityModel = useMemo(() => {
    if (!columnVisibility) return undefined;
    const model = { ...buildColumnVisibilityModel(specColumns), ...(columnVisibility.model ?? {}) };
    // Non-hideable columns follow the descriptor, never a (possibly stale) saved
    // user preference — a hideable:false column the user once toggled off would
    // otherwise stay hidden with no way to re-show it.
    (specColumns ?? []).forEach(col => {
      const { field, hideable, defaultHidden } = normalizeDescriptorColumn(col);
      if (hideable === false) model[field] = !defaultHidden;
    });
    return model;
  }, [columnVisibility, specColumns]);

  // --- Stat cards -----------------------------------------------------
  // The descriptor owns the card set, its labels, its tones, the filter a
  // click applies and how each card draws its numbers. The caller supplies
  // nothing for the conventional case: the numbers come from the resource's
  // own `<basePath>/stats/`, under the params the list is already reading.
  // A caller that already fetches the stats for other reasons (filter option
  // counts) passes `stats` straight through instead of a second fetch.
  const statsFetch = useMemo(
    () =>
      resolveStatsFetcher({
        basePath,
        fetchStats: statCards?.fetchStats,
        fetchStatsFn: config.fetchStatsFn,
      }) ?? NOOP_FETCH_STATS,
    [basePath, statCards?.fetchStats, config.fetchStatsFn],
  );
  const derivedStatsParams = useMemo(
    () => config.paramSerializer(effectiveExtraParams ?? {}),
    [config.paramSerializer, effectiveExtraParams],
  );
  const statsParams = statCards?.params ?? derivedStatsParams;
  const ownedStats = useGridStats(statsFetch, statsParams, {
    enabled: Boolean(statCards) && statCards.stats === undefined && statCards.enabled !== false,
  });
  const statsValue = statCards?.stats !== undefined ? statCards.stats : ownedStats.stats;
  const statsLoading = statCards?.stats !== undefined ? Boolean(statCards.isLoading) : ownedStats.isLoading;

  // Every card this user is allowed to see, labelled, in the order they sit in:
  // the tenant's configured order as the descriptor delivered it, then this
  // user's saved arrangement over that. Visibility is applied downstream rather
  // than here, so the configure dialog can still offer a card the user hid.
  const orderedStatCards = useMemo(() => {
    // Opt-in only: a page that declares no options.statCards keeps rendering
    // whatever it put in statsHeader, even when the descriptor carries cards.
    const specCards = descriptor?.stat_cards;
    if (!statCards || !Array.isArray(specCards) || specCards.length === 0) return [];
    const normalized = specCards.map(card => {
      const normalizedCard = normalizeDescriptorStatCard(card);
      return {
        ...normalizedCard,
        // Same resolver the column headers use, so a card's title and a
        // header cannot disagree about which terms copy may name.
        label: resolveDescriptorLabel(
          config.translate,
          normalizedCard.headerLabel,
          normalizedCard.headerFallback,
          terms,
        ),
        description: normalizedCard.tooltipLabel
          ? resolveDescriptorLabel(config.translate, normalizedCard.tooltipLabel, normalizedCard.tooltipFallback, terms)
          : undefined,
      };
    });
    return applyStatCardOrder(normalized, statCards?.order?.order);
  }, [descriptor, statCards, config.translate, terms]);

  // Same three-step precedence as columns: the descriptor's default_hidden set,
  // then the user's saved preference over it, then hideable:false re-forced from
  // the descriptor so a stale preference can never hide a locked card — nor
  // re-enable one the descriptor withheld, since only descriptor cards are
  // rendered at all.
  const statCardVisibilityModel = useMemo(
    () => ({
      ...buildStatCardVisibilityModel(descriptor?.stat_cards),
      ...(statCards?.visibility?.model ?? {}),
    }),
    [descriptor, statCards],
  );

  const visibleStatCards = useMemo(() => {
    if (orderedStatCards.length === 0) return [];
    // A card's filter routes through the grid's own model by default; the
    // onFilter/isActive pair stays for a page whose filter state cannot be
    // said in the descriptor's `{ param: value }` vocabulary at all.
    const applyFilter = statCards.onFilter ?? cardFilters.toggle;
    const readActive = statCards.isActive ?? cardFilters.isActive;
    // Clickability is a per-card question: a card that binds no filter is a
    // readout, and offering it a click would hand the reader a button that
    // changes nothing. A grid-level onFilter makes every card clickable, since
    // the page's filter state is then beyond the descriptor's vocabulary and
    // only the page can say which cards mean something.
    const isFilterControl = card => Boolean(statCards.onFilter) || hasStatCardFilterBinding([card]);
    // The params this row's cards own, so a click sets the card filter rather
    // than stacking onto whichever card was clicked before it.
    const ownedParams = statCardFilterParams(orderedStatCards);
    return orderedStatCards
      .filter(card => (card.hideable === false ? true : statCardVisibilityModel[card.cardId] !== false))
      .map(card => ({
        key: card.cardId,
        label: card.label,
        tooltip: card.description,
        display: config.resolveStatCardRenderer(card.type)(card, statsValue),
        tone: card.tone,
        loading: statsLoading,
        active: isFilterControl(card) ? readActive(card.filters, ownedParams) : false,
        onClick: isFilterControl(card) ? () => applyFilter(card.filters, ownedParams) : undefined,
      }));
  }, [
    orderedStatCards,
    statCardVisibilityModel,
    statCards,
    cardFilters,
    statsValue,
    statsLoading,
    config.resolveStatCardRenderer,
  ]);

  // --- Descriptor filters ----------------------------------------------
  // The descriptor names the filters this requester may use and, for a closed
  // one, every value it accepts; the numbers carry the per-option counts. Both
  // become filter-bar definitions over the grid's own card-filter model, so a
  // stat card and the control over the same param hold one value.
  //
  // A descriptor publishing no filters yields none, leaving a page's own
  // toolbar exactly as it was.
  const descriptorFilterDefs = useMemo(() => {
    const controls = buildDescriptorFilterControls(descriptor?.filters, statsValue?.filter_counts, {
      translate: config.translate,
      terms,
    });
    return buildToolbarFilterDefs(controls, cardFilters, descriptorIcon);
  }, [descriptor?.filters, statsValue?.filter_counts, config.translate, terms, cardFilters]);

  // A held filter value the descriptor no longer offers is dropped as soon as
  // the descriptor says so. The model outlives the vocabulary behind it — it is
  // remembered between visits and the vocabulary is per customer type — and the
  // grid refuses a value it does not offer, so a remembered one would answer
  // the whole page with an error rather than with rows.
  const staleFilterValues = useMemo(
    () => unofferedFilterValues(descriptor?.filters, cardFilters?.model),
    [descriptor?.filters, cardFilters?.model],
  );
  useEffect(() => {
    if (staleFilterValues.length === 0) return;
    for (const { param, value } of staleFilterValues) {
      const held = cardFilters.values(param);
      const kept = value === undefined ? [] : held.filter(each => String(each) !== value);
      cardFilters.setFilter(param, kept.length > 0 ? kept : null);
    }
  }, [staleFilterValues, cardFilters]);

  // Which handful of filters sit inline is the grid's own statement, so the
  // split arrives with the defs rather than being made again on every page.
  const { quick: quickFilterDefs, more: moreFilterDefs } = useMemo(
    () => splitQuickFilterDefs(descriptorFilterDefs),
    [descriptorFilterDefs],
  );

  // A page reads the descriptor's filters by taking toolbarContent as a
  // function: it owns where the two groups sit in its bar and which of its own
  // controls it still needs, which is a layout question the grid cannot
  // answer. `filters` is every published def in order, for a page that wants
  // to place them itself. A plain node stays a plain node.
  const resolvedToolbarContent =
    typeof toolbarContent === 'function'
      ? toolbarContent({
          filters: descriptorFilterDefs,
          quickFilters: quickFilterDefs,
          moreFilters: moreFilterDefs,
        })
      : toolbarContent;

  // A page taking toolbarContent as a node never sees the filters the
  // descriptor published for it: they are built, pruned of stale values, and
  // dropped. From the outside that looks like a grid the backend never gave
  // filters to, so it says so where the person who can fix it will read it.
  const discardedFilterCount = typeof toolbarContent === 'function' ? 0 : descriptorFilterDefs.length;
  useEffect(() => {
    if (discardedFilterCount === 0 || process.env.NODE_ENV === 'production') return;
    console.warn(
      `[EntityGrid] ${basePath}: the descriptor publishes ${discardedFilterCount} filter ` +
        'control(s) this page discards. Take toolbarContent as a function — ' +
        '({ quickFilters, moreFilters }) => … — to render them.',
    );
  }, [discardedFilterCount, basePath]);

  // --- Configure dialog ------------------------------------------------
  // Each section is offered only when the page wired somewhere to save it. A
  // grid that renders an arrangement but persists nothing would offer one that
  // dies on reload, so it keeps the data grid's own column chooser instead.
  const [isConfigureOpen, setIsConfigureOpen] = useState(false);

  // Columns the user may actually decide about: the generated selection
  // checkbox is position-locked, and the mobile card column is not a column
  // the reader thinks in.
  // A pinned column's position belongs to the pin, which the product does not
  // let a reader change, so it is offered for hiding but never for reordering.
  const pinnedFields = useMemo(() => {
    const pinned = buildPinnedColumns(specColumns);
    return new Set([...(pinned?.left ?? []), ...(pinned?.right ?? [])]);
  }, [specColumns]);

  const configurableColumns = useMemo(() => {
    if (!columnVisibility?.onChange) return null;
    const items = columns
      .filter(col => col.field !== GRID_CHECKBOX_SELECTION_FIELD && col.field !== MOBILE_SUMMARY_FIELD)
      .map(col => ({
        id: col.field,
        label: col.headerName || col.field,
        hideable: col.hideable !== false,
        // Without a persistKey there is nowhere to save an arrangement, so a
        // drag would be silently discarded — offer hiding only.
        isMovable: Boolean(columnOrderPrefKey) && !pinnedFields.has(col.field),
      }));
    return items.length ? { items, visibilityModel: effectiveColumnVisibilityModel ?? {} } : null;
  }, [columns, columnVisibility?.onChange, columnOrderPrefKey, effectiveColumnVisibilityModel, pinnedFields]);

  const configurableStatCards = useMemo(() => {
    if (!statCards?.visibility?.onChange || !statCards?.order?.onChange) return null;
    if (orderedStatCards.length === 0) return null;
    return {
      items: orderedStatCards.map(card => ({
        id: card.cardId,
        label: card.label,
        hideable: card.hideable !== false,
        description: card.description,
      })),
      visibilityModel: statCardVisibilityModel,
    };
  }, [orderedStatCards, statCards, statCardVisibilityModel]);

  const canConfigure = Boolean(configurableColumns || configurableStatCards);

  const handleConfigureSave = useCallback(
    result => {
      if (result.columns) {
        columnVisibility?.onChange?.(result.columns.visibilityModel);
        if (columnOrderPrefKey) setSavedColumnOrder(stampSavedColumnOrder(result.columns.order, columnStamp));
      }
      if (result.statCards) {
        statCards?.order?.onChange(result.statCards.order);
        statCards?.visibility?.onChange(result.statCards.visibilityModel);
      }
    },
    [columnVisibility, columnOrderPrefKey, setSavedColumnOrder, columnStamp, statCards],
  );

  // A caller-supplied statsHeader stays the escape hatch: pages that render
  // something other than the descriptor's cards (or nothing) keep owning that
  // slot, and only a page that opts into options.statCards gets the built one.
  const resolvedStatsHeader =
    statsHeader !== undefined ? (
      statsHeader
    ) : visibleStatCards.length ? (
      <GridStatCards cards={visibleStatCards} />
    ) : null;

  // Signal once the grid is mounted (apiRef.current is live). EntityGridContent
  // only mounts after the spec resolves, so a custom searchSlot that wires
  // useGridQuickFilter to a shared gridApiRef can (re)subscribe at that point —
  // on a cold spec load the grid mounts after the caller's first render. The
  // grid api ({ refresh, apiRef }) is handed back so callers that mutate rows
  // can refetch page 0; existing no-arg callers ignore it.
  useEffect(() => {
    onGridReady?.({ refresh: gridState.refresh, apiRef: gridState.apiRef });
  }, []);

  // Fires on every server-reported total change (initial fetch, then again on
  // every filter/sort/refresh that lands a new count) — for a caller that
  // mirrors the grid's live filtered total outside the grid itself (e.g. a
  // tab badge). `gridState.totalRowCount` starts null before the first fetch
  // resolves; callers that render it directly should fall back accordingly.
  useEffect(() => {
    onTotalRowCountChange?.(gridState.totalRowCount);
  }, [gridState.totalRowCount]);

  // Selection persistence across a remount (opt-in via selection.persistSelection),
  // e.g. a caller navigating to a detail/print route and back. Under infinite
  // scroll the grid id is the positional _gridIdx (see useServerDataGrid), which
  // is meaningless on a fresh mount — a row's real `id` is the only identity
  // that survives. Persist that real-id set to sessionStorage on every selection
  // change, and on mount resolve it back to whatever grid id each row is
  // assigned as pages arrive.
  const selectionKey = useGridSessionKey(persistKey, 'selection');
  const persistSelectionKey = selection?.persistSelection ? selectionKey : null;
  const pendingRestoreIdsRef = useRef();
  if (pendingRestoreIdsRef.current === undefined) {
    pendingRestoreIdsRef.current = persistSelectionKey ? readPersistedSelectionIds(persistSelectionKey) : [];
  }

  useEffect(() => {
    if (!persistSelectionKey) return undefined;
    const api = gridState.apiRef?.current;
    if (!api) return undefined;

    // Rows arrive append-only under lazy loading, so only the not-yet-scanned
    // tail needs checking on each 'rowsSet'; a shrink means the row map was
    // rebuilt (refetch/refilter) and scanning starts over. Without this, one
    // persisted id that never loads would force a full-map rescan on every
    // page arrival for the life of the mount.
    let scannedRowCount = 0;
    const restorePendingSelection = () => {
      if (!pendingRestoreIdsRef.current.length) return;
      const rowModels = api.getRowModels?.();
      if (!rowModels?.size) return;
      if (rowModels.size < scannedRowCount) scannedRowCount = 0;
      if (rowModels.size === scannedRowCount) return;
      const pending = new Set(pendingRestoreIdsRef.current);
      const matchedGridIds = [];
      let rowIndex = 0;
      rowModels.forEach((row, gridId) => {
        if (rowIndex++ < scannedRowCount) return;
        if (pending.has(row?.id)) {
          matchedGridIds.push(gridId);
          pending.delete(row.id);
        }
      });
      scannedRowCount = rowModels.size;
      pendingRestoreIdsRef.current = Array.from(pending);
      if (matchedGridIds.length) api.selectRows?.(matchedGridIds, true);
    };

    const persistCurrentSelection = model => {
      // Select-all ('exclude' with a possibly-huge implicit id set) has no
      // stable id list to restore positionally — only an explicit 'include'
      // selection is worth persisting.
      if (model.type !== 'include') return;
      const ids = Array.from(model.ids ?? [])
        .map(gridId => api.getRow?.(gridId)?.id)
        .filter(id => id != null);
      writePersistedSelectionIds(persistSelectionKey, ids);
    };

    restorePendingSelection(); // covers rows already fetched by the time this runs
    const unsubscribeRows = api.subscribeEvent?.('rowsSet', restorePendingSelection);
    const unsubscribeSelection = api.subscribeEvent?.('rowSelectionChange', persistCurrentSelection);
    return () => {
      unsubscribeRows?.();
      unsubscribeSelection?.();
    };
  }, [persistSelectionKey, gridState.apiRef]);

  // Thread the grid's apiRef into the caller's action renderer so positional
  // infinite-scroll ids resolve to rows (apiRef.getRow) — EntityGrid owns the
  // grid state, so the caller can't reach apiRef otherwise.
  const selectionActions = selection?.actions
    ? (sel, clearSelection) => selection.actions(sel, clearSelection, gridState.apiRef)
    : undefined;

  const composedToolbarActions = exportOptions ? (
    <>
      {toolbarActions}
      <DownloadButton
        tooltip={config.translate('actions.export', 'Export')}
        onClick={runExport}
        loading={isExporting}
      />
    </>
  ) : (
    toolbarActions
  );

  return (
    <>
      <ServerDataGrid
        gridState={gridState}
        columns={columns}
        rows={rows}
        mobileCards={Boolean(mobile?.renderCard)}
        onRowClick={onRowClick}
        showSearch={showSearch}
        searchPlaceholder={searchPlaceholder}
        searchSlot={searchSlot}
        countNoun={countNoun}
        createAction={createAction}
        toolbarContent={resolvedToolbarContent}
        toolbarActions={composedToolbarActions}
        toolbarVariant={toolbarVariant}
        showGridSettings={showGridSettings}
        onConfigure={canConfigure ? () => setIsConfigureOpen(true) : undefined}
        checkboxSelection={checkboxSelection}
        selectionActions={selectionActions}
        selectAllActive={selection?.selectAll?.active}
        onSelectAllRequest={selection?.selectAll?.onRequest}
        onSelectAllClear={selection?.selectAll?.onClear}
        columnVisibilityModel={effectiveColumnVisibilityModel}
        onColumnVisibilityModelChange={columnVisibility?.onChange}
        onColumnOrderChange={columnOrderPrefKey ? handleColumnOrderChange : undefined}
        onColumnWidthChange={columnWidthPrefKey ? handleColumnWidthChange : undefined}
        initialState={initialState}
        onDataSourceError={handleDataSourceError}
        {...gridProps}
      >
        {resolvedStatsHeader}
        {dataSourceError && (
          <Alert severity="error" onClose={() => setDataSourceError(null)} sx={{ mx: 2, mt: 1 }}>
            {config.translate('entities.rowsLoadError', 'Failed to load rows. Please try again.')}
          </Alert>
        )}
        {exportError && (
          <Alert severity="error" onClose={() => setExportError(null)} sx={{ mx: 2, mt: 1 }}>
            {config.translate('entities.exportError', 'Export failed. Please try again.')}
          </Alert>
        )}
      </ServerDataGrid>

      {isConfigureOpen && (
        <GridConfigureDialog
          open
          onClose={() => setIsConfigureOpen(false)}
          columns={configurableColumns}
          statCards={configurableStatCards}
          onSave={handleConfigureSave}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Outer: spec loading + the public API
// ---------------------------------------------------------------------------

/**
 * Generic MUI X DataGrid Pro backed by an odl_app list endpoint and its
 * companion `/grid-spec/` descriptor.
 *
 * @param {object}   props
 * @param {string}   props.basePath       odl_app resource path (no `odl-app/`
 *                                        prefix, no trailing slash), e.g.
 *                                        `'sites/1/cameras'`. Drives both the
 *                                        list fetch and the `/grid-spec/` call.
 * @param {object}   [props.fieldOverrides]  `{ [field]: Partial<GridColDef> }`
 *                                        render overrides merged over the
 *                                        descriptor's column defs.
 * @param {Array}    [props.rows]         In-memory row array. When provided
 *                                        (including `[]`), the grid renders this
 *                                        array directly and skips the server list
 *                                        fetch entirely. Mutually exclusive with
 *                                        `fetchFn`/`extraParams` (server-only).
 *                                        Columns and widths still come from the
 *                                        `/grid-spec/` descriptor. Gate is
 *                                        `rows !== undefined` — an empty array is
 *                                        a valid "no active rows" state.
 * @param {Function} [props.fetchFn]      Optional list-fetch override
 *                                        `(params) => Promise<{ data, meta }>`,
 *                                        for endpoints whose rows are unioned or
 *                                        mapped. Defaults to GET `<basePath>/`.
 *                                        Memoize it. Not used when `rows` is set.
 * @param {object}   [props.actionHandlers]  `{ [key]: (row) => void | { onClick, variant: 'button' } }`
 *                                        for the descriptor's `actions` columns. A bare handler
 *                                        renders the default icon button; `{ onClick, variant: 'button' }`
 *                                        renders a labeled contained button for that action.
 * @param {object}   [props.extraParams]  Query params merged into every list
 *                                        request (filter values live here). Must
 *                                        be memoized by the caller.
 * @param {object}   [props.specParams]   Scope-only params for descriptor
 *                                        resolution; defaults to `extraParams`.
 *                                        Pass the stable scope subset (customer,
 *                                        plant, …) so filter changes reuse the
 *                                        cached `/grid-spec/` descriptor instead
 *                                        of refetching it. Must be memoized by
 *                                        the caller.
 * @param {object}   [props.options]      Chrome + interaction bag, forwarded to
 *                                        ServerDataGrid:
 *   - `showSearch` (bool, default true), `searchPlaceholder` (string),
 *     `searchSlot` (node — replaces the quick-filter input).
 *   - `countNoun` (string — already-translated plural noun for the row count,
 *     e.g. "batches" → "24 batches"; defaults to "items").
 *   - `createAction` ({ label, onClick }), `toolbarContent` (node — the
 *     caller's filter bar — or `({ filters }) => node`, where `filters` are the
 *     descriptor's published filter controls as filter-bar definitions, each
 *     already wired to the grid's card-filter model by param; `[]` while the
 *     descriptor publishes none), `toolbarActions` (node).
 *   - `persistKey` (string), `infiniteScroll` (bool, default false),
 *     `defaultSort` ([{ field, sort }] — overrides the descriptor default).
 *   - `onRowClick` ((params) => void).
 *   - `gridApiRef` (MUI X apiRef — share the grid's apiRef with a custom
 *     searchSlot or sibling that needs it; EntityGrid creates one otherwise).
 *   - `onGridReady` (({ refresh, apiRef }) => void — fired once the grid mounts
 *     and apiRef.current is live; pair with gridApiRef when a searchSlot must
 *     subscribe to it, or capture `refresh` to refetch page 0 after a mutation).
 *   - `onTotalRowCountChange` ((count: number|null) => void — fires on every
 *     server-reported total change, so a caller can mirror the grid's live
 *     filtered row count outside the grid itself, e.g. a tab badge; null
 *     before the first fetch resolves).
 *   - `selection` ({ enabled, actions(selection, clear, apiRef),
 *     selectAll: { active, onRequest, onClear }, persistSelection }).
 *     `persistSelection` (bool, default false — requires `persistKey`) keeps the
 *     checkbox selection across a remount (e.g. navigating away and back) by
 *     persisting each selected row's real `id` to sessionStorage and re-resolving
 *     it against the row's grid id as pages load; a select-all selection isn't
 *     persisted.
 *   - `statsHeader` (node — rendered above the toolbar). The caller-owned
 *     escape hatch for the same slot `statCards` fills: a node replaces the
 *     descriptor's cards and `null` drops them, so a page rendering something
 *     else there (or nothing) keeps owning it. Absent (undefined) lets
 *     `statCards` build the header.
 *   - `statCards` ({ fetchStats, params, stats, isLoading, enabled, visibility,
 *     filters, onFilter, isActive } — opt into the descriptor's own aggregate
 *     cards. Its presence is the switch, and `{}` is a complete declaration:
 *     without it a descriptor's `stat_cards` render nothing, so a page that
 *     never showed cards keeps not showing them. The descriptor owns the card
 *     set, labels, tones, how each card draws its numbers, and the filter a
 *     click applies.
 *     Numbers default to the resource's own `<basePath>/stats/` under the
 *     params the list is reading. `fetchStats` overrides the endpoint for a
 *     resource whose numbers live elsewhere, `params` overrides the query
 *     (pass the scope-only subset when the cards must not narrow with the
 *     grid's own filters), `enabled: false` holds the fetch off, and `stats`
 *     (+ `isLoading`) passes through a payload the page already fetched for
 *     other reasons so it is not fetched twice.
 *     `visibility` is `{ model, onChange }` from useStatCardVisibilityModel.
 *     `filters` is a useGridCardFilters model — pass the page's own when it
 *     renders filter chips over the same params a card writes, so the two
 *     drive one value; omit it and the grid owns the model and filters itself.
 *     `onFilter(filters, ownedParams)` / `isActive(filters)` are the escape
 *     hatch for filter state that cannot be expressed as `{ param: value }`
 *     pairs at all; `filters` is every pair the clicked card names and
 *     `ownedParams` the param set the row's cards own, which a click replaces
 *     rather than adds to).
 *   - `terms` (object — the tenant's vocabulary, handed to the translator as
 *     to the tenant's own word, so a column header or card label reading
 *     `{{term}}` renders "Panels"/"Zones"/"Faces" without the page
 *     re-headering it by hand).
 *   - `toolbarVariant` ('elevated' default | 'flat' — 'flat' for a grid inside
 *     a card, tab or blade, where the elevated toolbar reads as a second
 *     surface stacked on the first).
 *   - `excludeFields` (string[] — descriptor fields to drop from the grid
 *     entirely, for a mount whose scope makes a column redundant, e.g. the
 *     customer column inside a single-customer route).
 *   - `mobile` ({ renderCard(row) => node } — single-column mobile view).
 *   - `extraColumns` (GridColDef[] — bespoke columns the descriptor doesn't
 *     own, e.g. an action button; appended after the descriptor columns,
 *     desktop only).
 *   - `columnVisibility` ({ model, onChange } — controlled overrides layered
 *     over descriptor defaults). Pinning has no caller option: the descriptor
 *     owns it, and the selection checkbox pins with it automatically.
 *   - `showGridSettings` (bool, default true — ServerDataGrid's Tune/columns
 *     button; set false when a caller renders its own grid-settings entry
 *     point elsewhere, so ServerDataGrid's toolbar isn't forced on just to
 *     host it).
 *   - `export` ({ fileName, columns, extraColumns } — opt into the toolbar's CSV
 *     export. Its presence is the switch; every key is optional. The file's
 *     columns land in backend-descriptor order: the ones currently on screen,
 *     plus every column the descriptor marks `export: "always"`, minus the ones
 *     it marks `export: false` — and it holds every row matching the grid's
 *     live filters/search/sort rather than the visible page.
 *     `fileName` defaults to the resource name (`'customers.csv'`). `columns` is
 *     `{ [field]: { headerName?, valueGetter? } }` for a value whose export text
 *     differs from its raw form — reach for it only when the descriptor type
 *     cannot say it, since the descriptor is the single source of what exports
 *     and where. `extraColumns` ([{ field, headerName, valueGetter }]) appends
 *     columns the descriptor does not own — a value the client computes from a
 *     nested object — and they answer to no visibility toggle).
 *   - `gridProps` (object — raw DataGridPro/ServerDataGrid escape hatch,
 *     spread last).
 */
export default function EntityGrid({
  basePath,
  fieldOverrides,
  fetchFn,
  actionHandlers,
  extraParams,
  specParams,
  rows,
  options = {},
}) {
  const config = useDataGridConfig();
  const { specColumns, descriptor, isLoading, isError } = useGridSpec(basePath, specParams ?? extraParams);

  // options.excludeFields: descriptor columns this surface drops entirely
  // (not just hidden — gone from the grid and the column chooser). The row
  // payload is untouched, so renderCells can still read the field.
  const { excludeFields } = options;
  const effectiveSpecColumns = useMemo(() => {
    if (!excludeFields?.length || !Array.isArray(specColumns)) return specColumns;
    return specColumns.filter(col => !excludeFields.includes(col.field));
  }, [specColumns, excludeFields]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, p: 4 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography color="error">
          {config.translate('entities.specLoadError', 'Failed to load grid configuration.')}
        </Typography>
      </Box>
    );
  }

  return (
    <EntityGridContent
      basePath={basePath}
      descriptor={descriptor}
      specColumns={effectiveSpecColumns}
      fieldOverrides={fieldOverrides}
      fetchFn={fetchFn}
      actionHandlers={actionHandlers}
      extraParams={extraParams}
      rows={rows}
      options={options}
    />
  );
}
